import { defineComponent as _defineComponent } from 'vue'
import {
  computed, PropType, ref, watch
} from 'vue';
import BCXAbsoluteTransition from '@/components/molecules/BCXAbsoluteTransition.vue';
import useFormattedDateTime from '@/mixins/useFormattedDateTime';
import BCXDatePickerMonth from '@/components/molecules/forms/datepicker/BCXDatePickerMonth.vue';
import BCXDatePickerNavigation from '@/components/molecules/forms/datepicker/BCXDatePickerNavigation.vue';
import { useVModel } from '@vueuse/core';
import BCXDatePickerYear from '@/components/molecules/forms/datepicker/BCXDatePickerYear.vue';
import BCXDatePickerYears from '@/components/molecules/forms/datepicker/BCXDatePickerYears.vue';
import { LONG_MONTHS } from '@/models/DateTime';
import useI18n from '@/mixins/useI18n';
import { BcxDatePickerTimeLevel } from '@/models/BcxDatePicker';


export default /*#__PURE__*/_defineComponent({
  __name: 'BCXDatePicker',
  props: {
  value: {
    type: [String, Date] as PropType<string | Date>,
    default: null
  },
  earliest: {
    type: [String, Date],
    default: null
  },
  latest: {
    type: [String, Date],
    default: null
  },
  fromYear: {
    type: Number,
    default: null
  },
  toYear: {
    type: Number,
    default: null
  },
  proposedDate: {
    type: [String, Date] as PropType<string | Date>,
    default: null
  },
  // Days before this date are shown visually dimmed (looks disabled) but stay selectable and are not clamped.
  dimBefore: {
    type: [String, Date] as PropType<string | Date>,
    default: null
  },
  showSearchButton: {
    type: Boolean,
    default: false
  },
  hasDot: {
    type: Function as PropType<(date: Date) => boolean>,
    default: null
  },
  variant: {
    type: String as PropType<'default' | 'small'>,
    default: 'default'
  },
  // 'bottom' keeps the existing layout; 'top' renders the Today/Search actions above the calendar
  actionsPosition: {
    type: String as PropType<'top' | 'bottom'>,
    default: 'bottom'
  }
},
  emits: ['update:value', 'select-timelevel', 'search', 'month-change'],
  setup(__props, { emit }) {

const props = __props;





const { locale } = useI18n();

const value = useVModel(props);

const { getFormattedDate } = useFormattedDateTime();

const triggerTransitionCounter = ref(0);
const isSlidingLeft = ref(false);

const timeLevel = ref<BcxDatePickerTimeLevel>(BcxDatePickerTimeLevel.MONTH);

const initialDate = computed(() => new Date(props.proposedDate || props.value)); // When opening, the proposed date should always be displayed, if available.

const currentDate = ref(new Date(props.value));
const earliestDate = ref(new Date(props.earliest ?? new Date(1970, 0, 1)));
const latestDate = ref(new Date(props.latest ?? new Date(2050, 11, 31)));
const dimBeforeDate = computed(() => (props.dimBefore ? new Date(props.dimBefore) : null));

const earliestYear = computed(() => props.fromYear ?? earliestDate.value.getFullYear());
const latestYear = computed(() => props.toYear ?? latestDate.value.getFullYear());

const clampedDate = (date: Date) => {
  if (date < earliestDate.value) {
    date = earliestDate.value;
  } else if (date > latestDate.value) {
    date = latestDate.value;
  }
  return date;
};

watch(value, (value) => {
  if (!value) {
    value = new Date();
  }
  currentDate.value = clampedDate(new Date(value));
}, { immediate: true });

const currentYear = computed(() => (triggerTransitionCounter.value === 0 ? initialDate.value.getFullYear() : currentDate.value.getFullYear())); // The initial date should always be displayed when opening.
const currentMonthIndex = computed(() => (triggerTransitionCounter.value === 0 ? initialDate.value.getMonth() : currentDate.value.getMonth())); // The initial date should always be displayed when opening.
const currentDay = computed(() => currentDate.value.getDate());

const incTime = (amount: number) => {
  let nextDate: Date;
  switch (timeLevel.value) {
    case BcxDatePickerTimeLevel.MONTH:
      nextDate = new Date(currentYear.value, currentMonthIndex.value + amount, currentDay.value);
      break;
    case BcxDatePickerTimeLevel.YEAR:
      nextDate = new Date(currentYear.value + amount, currentMonthIndex.value, currentDay.value);
      break;
  }

  // if (nextDate >= earliestDate.value && nextDate <= latestDate.value) {
  currentDate.value = nextDate;
  triggerTransitionCounter.value++;
  isSlidingLeft.value = amount > 0;
  // } else {
  //   currentDate.value = clampedDate(nextDate);
  //   console.log('Out of bounds!');
  // }
};

const onPrev = () => {
  incTime(-1);
};

const onNext = () => {
  incTime(1);
};

const onAdvanceTimeLevel = () => {
  timeLevel.value++;
  if (timeLevel.value > BcxDatePickerTimeLevel.YEARS) { timeLevel.value = BcxDatePickerTimeLevel.MONTH; }
};

const navigationLabel = computed(() => {
  switch (timeLevel.value) {
    case BcxDatePickerTimeLevel.MONTH: return `${LONG_MONTHS[locale.value][currentMonthIndex.value]} ${currentYear.value}`;
    case BcxDatePickerTimeLevel.YEAR: return `${currentYear.value}`;
  }
  return '';
});

const onSelectDate = (date: Date) => {
  if (typeof props.value === 'string') {
    value.value = clampedDate(date).toISOString().slice(0, 10);
  } else {
    value.value = clampedDate(date);
  }
  emit('select-timelevel', timeLevel.value);
  timeLevel.value = BcxDatePickerTimeLevel.MONTH;
};

const onSelectToday = () => {
  currentDate.value = clampedDate(new Date());
  value.value = currentDate.value;
  timeLevel.value = BcxDatePickerTimeLevel.MONTH; // switch back to the day view, otherwise the user is stuck in month/year view
};

watch([currentYear, currentMonthIndex], () => {
  emit('month-change', new Date(currentYear.value, currentMonthIndex.value, 1));
});

return { __sfc: true,props, emit, locale, value, getFormattedDate, triggerTransitionCounter, isSlidingLeft, timeLevel, initialDate, currentDate, earliestDate, latestDate, dimBeforeDate, earliestYear, latestYear, clampedDate, currentYear, currentMonthIndex, currentDay, incTime, onPrev, onNext, onAdvanceTimeLevel, navigationLabel, onSelectDate, onSelectToday, BCXAbsoluteTransition, BCXDatePickerMonth, BCXDatePickerNavigation, BCXDatePickerYear, BCXDatePickerYears, BcxDatePickerTimeLevel }
}

})